import { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Icon from 'src/components/Icon';
import {
  ACCOUNT_PASSWORD_VALIDATE_ERROR,
  LOGIN_EMAIL_VALIDATE_ERROR,
  LOGIN_PASSWORD_VALIDATE_ERROR,
  VALID_STATE,
} from 'src/constants/validate';

type Props = {
  mode: string;
  label: string;
  name: string;
  type?: string;
  inputClassName?: string;
  inputAttr?: {
    [key: string]: any;
  };
  validate?: Record<string, (value: string) => boolean | Promise<boolean>>;
  errors?: Record<string, string>;
  errorStyle?: Record<string, string>;

  value: string;
  setValue: (value: string) => void;
  validState: VALID_STATE;
  setValidState: (value: VALID_STATE) => void;
  errorMessage: string;
  setErrorMessage: (value: string) => void;
  onValidate: (
    value: string,
    validate?: Record<string, (value: string) => boolean | Promise<boolean>>,
    errors?: Record<string, string>,
  ) => Promise<void>;
};

type ReturnType = {
  value: string;
  isValid: boolean;
  CustomFormInput: JSX.Element;
  setValue: (value: string) => void;
  triggerValidate: () => void;
  resetValidate: () => void;
};

const FormInput = ({
  mode,
  label,
  type,
  name,
  inputClassName,
  inputAttr,
  validate,
  errors,
  value,
  setValue,
  validState,
  errorMessage,
  onValidate,
  errorStyle,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  const onChange = useCallback(
    (val: string) => {
      setValue(val);
      if (mode === 'change') {
        onValidate && onValidate(val, validate, errors);
      }
    },
    [validate, errors],
  );

  const onClickVisible = useCallback(
    (e) => {
      e.preventDefault();
      setIsVisible(!isVisible);
    },
    [isVisible],
  );

  return (
    <section className={['flex', 'flex-col', 'w-full'].join(' ')}>
      <label htmlFor={name} className="block text-xs text-greyish-brown">
        {t(label as string)}
      </label>
      <div
        className={[
          'flex',
          'border',
          'relative',
          'justify-between',
          'items-center',
          label ? 'mt-1.5' : '',
          'rounded-sm',
          'focus:border-black',
          validState === VALID_STATE.NONE && 'border-ice-blue focus:border-ice-blue',
          validState === VALID_STATE.ERROR && 'border-purpley-pink focus:border-purpley-pink',
          validState === VALID_STATE.PASS && 'border-bluey-purple focus:border-bluey-purple',
        ].join(' ')}
      >
        <input
          id={name}
          name={name}
          type={(type === 'password' ? (isVisible ? 'text' : type) : type) || 'text'}
          className={
            ['text-sm', 'bg-white', 'text-black', 'placeholder-brown-grey', 'block', 'w-full', 'py-3', 'px-4'].join(' ') +
            (inputClassName ? ' ' + inputClassName : '')
          }
          value={value}
          onChange={(e) => onChange(e.target.value)}
          {...inputAttr}
          placeholder={inputAttr && inputAttr.placeholder && t(inputAttr.placeholder)}
        />
        {type === 'password' && (
          <div className="mr-4" onClick={onClickVisible}>
            <Icon name={isVisible ? 'visible' : 'invisible'} size="20"></Icon>
          </div>
        )}
        {validState === VALID_STATE.PASS && (
          <Icon name="checked" size="20" className={`absolute pointer-events-none ${type === 'password' ? 'right-12' : 'right-4'}`} />
        )}
      </div>
      {validState === VALID_STATE.ERROR && (
        <div className="flex items-start text-purpley-pink text-xs mt-2" style={errorStyle ? errorStyle : {}}>
          <Icon name="delete" size="16" className="mr-1.5" />
          <Trans i18nKey={errorMessage}></Trans>
        </div>
      )}
    </section>
  );
};
export default FormInput;

export function useFormInput(options: Partial<Props>): ReturnType {
  const [value, setValue] = useState('');
  const [validState, setValidState] = useState(VALID_STATE.NONE);
  const [errorMessage, setErrorMessage] = useState('');

  const onValidate = useCallback(async (val, validate, errors) => {
    // console.log(val)
    if (validate) {
      const validateList = await Promise.all(
        Object.keys(validate).map(async (key) => {
          if (validate && validate[key]) {
            return !(await validate[key](val)) ? key : null;
          }
        }),
      );
      const resultList = validateList.filter((val) => !!val);
      if (resultList.length > 0 && resultList[0]) {
        setValidState(VALID_STATE.ERROR);
        errors && errors[resultList[0]] && setErrorMessage(errors[resultList[0]]);
      } else {
        setValidState(VALID_STATE.PASS);
        setErrorMessage('');
      }
    }
  }, []);

  const triggerValidate = () => onValidate(value, options.validate, options.errors);
  const resetValidate = () => {
    setValidState(VALID_STATE.NONE);
    setErrorMessage('');
  };

  return {
    value,
    isValid: validState === VALID_STATE.PASS,
    setValue,
    triggerValidate,
    resetValidate,
    CustomFormInput: (
      <FormInput
        {...{
          mode: 'change',
          label: '',
          name: 'text',
          type: 'text',
          value,
          setValue,
          validState,
          setValidState,
          errorMessage,
          setErrorMessage,
          onValidate,
          ...(options || {}),
        }}
      />
    ),
  };
}

export const useFormInputEmail = (options?: Partial<Props>): ReturnType =>
  useFormInput({
    label: 'login/email_title',
    name: 'email',
    type: 'text',
    validate: {
      required: (value: string) => !!value,
      pattern: (value: string) => {
        const regex = new RegExp(
          /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/,
        );
        return !!value.match(regex);
      },
    },
    inputAttr: {
      placeholder: 'login/email_placeholder',
    },
    errors: LOGIN_EMAIL_VALIDATE_ERROR,
    ...options,
  });

export const useFormInputPassword = (options?: Partial<Props>): ReturnType =>
  useFormInput({
    label: 'login/password_title',
    name: 'password',
    type: 'password',
    validate: {
      required: (value: string) => !!value,
    },
    errors: LOGIN_PASSWORD_VALIDATE_ERROR,
    inputAttr: {
      placeholder: 'login/password_placeholder',
    },
    ...options,
  });

export const useFormInputAccountPassword = (options?: Partial<Props>): ReturnType =>
  useFormInput({
    label: 'login/password_title',
    name: 'password',
    type: 'password',
    inputAttr: {
      placeholder: 'account/form/password_placeholder',
    },
    errors: ACCOUNT_PASSWORD_VALIDATE_ERROR,
    validate: {
      required: (value: string) => !!value,
      pattern: (value) => {
        const regex = new RegExp(/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/);
        return !!value.match(regex);
      },
      min: (value) => {
        return value.length >= 8;
      },
    },
    ...options,
  });
